import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Chart from "react-apexcharts";
import { getMilestoneTrend, getUpdateRunList } from "../../api/process";
import SidePanel from "../../components/common/layout/SidePanel";
import MilestoneSelector from "../../components/process/MilestoneSelector";
import PageHeader from "../../components/common/ui/PageHeader";

export default function MTAChart() {
  const [milestones, setMilestones] = useState([]);
  const [projectPeriod, setProjectPeriod] = useState({ start: 0, end: 0 });
  const [selectedIds, setSelectedIds] = useState([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const { id: projectId } = useParams();
  const [showDetails, setShowDetails] = useState(false);
  const [baselineRuns, setBaselineRuns] = useState([]);
  const [selectedBaseline, setSelectedBaseline] = useState(null);

  useEffect(() => {
    getUpdateRunList().then((runs) => {
      setBaselineRuns(runs.filter((r) => r.run_type === "B"));

      // ✅ 기본값: 가장 최근 항목
      if (runs.length > 0) {
        const latestBaseline = runs.find((r) => r.run_type === "B");
        setSelectedBaseline(latestBaseline?.id || null);
      }
    });
  }, []);

  useEffect(() => {
    getMilestoneTrend(selectedBaseline).then((res) => {
      const groups = res.wbs_groups || [];
      const allMilestones = groups.flatMap((g) => g.milestones || []);

      setMilestones(allMilestones);
      setGroups(groups);
      setProjectPeriod({
        start: new Date(res.project.period_start).getTime(),
        end:
          new Date(res.project.period_finish).getTime() +
          30 * 24 * 60 * 60 * 1000,
      });

      const allIds = groups.flatMap((g) => g.milestones.map((m) => m.code));
      setSelectedIds(allIds);
    });
  }, [projectId, selectedBaseline]);

  if (!milestones.length) return (
    <div className="p-6">
      <PageHeader>MTA 분석</PageHeader>
      <p>로딩 중...</p>
    </div>);

  // ✅ 선택된 마일스톤만 필터링
  const filtered = milestones.filter((ms) => selectedIds.includes(ms.code));

  // ✅ 각 마일스톤 시리즈 생성 + 상태별 색상 + 툴팁 정보
  const milestoneSeries = filtered.map((ms) => {
    const points = ms.updates
      .map((u) => ({
        x: u.cutoff === 0 ? projectPeriod.start : new Date(u.cutoff).getTime(),
        y: new Date(u.forecast).getTime(),
      }))
      .sort((a, b) => a.x - b.x);

    const clipped = [];

    for (let i = 0; i < points.length; i++) {
      const curr = points[i];
      if (i === 0) {
        clipped.push(curr);
        continue;
      }

      const prev = points[i - 1];
      if (curr.y >= curr.x) {
        clipped.push(curr);
        continue;
      }

      const t = (prev.x - prev.y) / (curr.y - prev.y - (curr.x - prev.x));
      const x_cross = prev.x + (curr.x - prev.x) * t;
      const y_cross = x_cross;
      clipped.push({ x: x_cross, y: y_cross });
      break;
    }

    // ✅ 상태별 색상 계산 (Baseline 기준)
    const baseline = new Date(ms.baseline).getTime(); // 기준선 finish
    const lastForecast = new Date(ms.updates.at(-1)?.forecast).getTime();
    const diffDays = Math.round(
      (lastForecast - baseline) / (1000 * 60 * 60 * 24)
    );

    let color = "#22c55e"; // 기본: 정상(초록)
    let status = "정상";
    if (diffDays > 0) {
      color = "#ef4444"; // 지연(빨강)
      status = `지연 (${diffDays}일)`;
    } else if (diffDays < 0) {
      color = "#3b82f6"; // 조기(파랑)
      status = `조기 (${Math.abs(diffDays)}일)`;
    } else {
      status = "정상";
    }

    // ✅ 각 포인트에 툴팁용 상태 추가
    const dataWithStatus = clipped.map((p) => ({ ...p, status }));

    return { name: ms.name, data: dataWithStatus, color, status };
  });

  // ✅ 대각선 (Completion Line)
  const completionLine = {
    name: "Completion Line",
    data: [
      { x: projectPeriod.start, y: projectPeriod.start },
      { x: projectPeriod.end, y: projectPeriod.end },
    ],
    color: "#ef4444",
  };

  // ✅ ApexCharts 옵션
  const options = {
    chart: {
      type: "line",
      animations: { enabled: false },
      zoom: { enabled: false },
      toolbar: { show: false },
    },
    markers: showDetails
      ? {
        size: 4, // 포인트 보이게
        hover: { size: 7 },
      }
      : {
        size: 0, // 선만 표시
        hover: { size: 0 },
      },

    grid: {
      padding: { left: 10, right: 10 },
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
    stroke: {
      width: [...milestoneSeries.map(() => 1), 2],
      curve: "straight",
      dashArray: [...milestoneSeries.map(() => 0), 0],
    },
    colors: milestoneSeries.map((s) => s.color).concat("#ef4444"),
    legend: { show: false },

    // ✅ 툴팁 커스텀
    tooltip: {
      enabled: showDetails,
      shared: false,
      intersect: true,
      followCursor: true,
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        const seriesItem = w.config.series[seriesIndex];
        const point = seriesItem.data[dataPointIndex];
        if (!point) return "";

        // ✅ baseline & forecast 날짜 계산
        const baseline = new Date(
          filtered.find((ms) => ms.name === seriesItem.name)?.baseline
        ).toLocaleDateString("ko-KR", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

        const forecast = new Date(point.y).toLocaleDateString("ko-KR", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

        const status = point.status || "정보 없음";

        return `
      <div style="
        padding:6px 10px;
        border-radius:8px;
        background:#fff;
        box-shadow:0 0 6px rgba(0,0,0,0.1);
        font-size:12px;
        line-height:1.4;
      ">
        <strong>${seriesItem.name}</strong><br/>
        Baseline 완료일: <b>${baseline}</b><br/>
        현재 예측 완료일: <b>${forecast}</b><br/>
        최근 상태: <b>${status}</b>
      </div>
    `;
      },
    },
    xaxis: {
      type: "datetime",
      position: "top",
      min: projectPeriod.start,
      max: projectPeriod.end,
      tickAmount: 10,
      title: { text: "Cut-off Date" },
      labels: {
        formatter: (val) => {
          const d = new Date(val);
          return `${String(d.getFullYear()).slice(2)}/${String(
            d.getMonth() + 1
          ).padStart(2, "0")}`;
        },
      },
    },
    yaxis: {
      type: "datetime",
      min: projectPeriod.start,
      max: projectPeriod.end,
      tickAmount: 10,
      // title: { text: "Forecast Finish Date" },
      labels: {
        formatter: (val) => {
          const d = new Date(val);
          return `${String(d.getFullYear()).slice(2)}/${String(
            d.getMonth() + 1
          ).padStart(2, "0")}`;
        },
      },
    },
  };

  // ✅ 마일스톤 가이드선 (연한선)
  const guideLines = milestoneSeries.map((s) => {
    const ms = filtered.find((m) => m.name === s.name);
    if (!ms || !ms.baseline || !s.data.length) return null;

    const baselineTime = new Date(ms.baseline).getTime();
    const firstForecast = s.data[0];

    return {
      name: `${s.name}-guide`,
      data: [
        { x: baselineTime, y: baselineTime }, // baseline point (on completion line)
        { x: firstForecast.x, y: firstForecast.y }, // first forecast
      ],
      color: "rgba(100,100,100,0.25)",
      isGuide: true,
    };
  }).filter(Boolean);


  return (
    <div className="p-6">
      <PageHeader>MTA 분석</PageHeader>
      <div className="p-4 bg-white border rounded-xl flex flex-col items-center">
        <div className="flex flex-wrap items-center justify-between w-full mb-3 border-b border-gray-200 pb-2">
          {/* 왼쪽: 제목 */}
          <div>
            <h2 className="text-base font-medium text-slate-700">
              Milestone Trend
            </h2>
          </div>

          {/* 오른쪽: 컨트롤 그룹 */}
          <div className="flex items-center gap-3 text-sm text-slate-600">
            {/* 마일스톤 선택 버튼 */}
            {/* <button
              onClick={() => setIsPanelOpen(true)}
              className="px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-100 
                 text-gray-700 text-sm transition"
            >
              마일스톤 선택
            </button> */}

            {/* 상세정보 토글 */}
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                className="accent-indigo-500 w-3.5 h-3.5"
                checked={showDetails}
                onChange={() => setShowDetails(!showDetails)}
              />
              <span className="text-body">상세보기</span>
            </label>

            {/* 구분선 */}
            <div className="w-px h-4 bg-gray-300 mx-1" />

            {/* 기준선 선택 */}
            <div className="flex items-center gap-1">
              <span className="text-body mr-2">기준선</span>
              <select
                value={selectedBaseline || ""}
                onChange={(e) => setSelectedBaseline(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-1 text-xs text-slate-700 
                   focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 transition"
              >
                {baselineRuns.map((r) => (
                  <option key={r.id} value={r.id}>
                    Rev.{r.revision || "Unnamed"}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <Chart
          options={options}
          series={[...milestoneSeries, ...guideLines, completionLine]}
          type="line"
          width={1400}
          height={600}
        />
      </div>
    </div>

  );
}
